//Intro modal

import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { motion, AnimatePresence } from 'framer-motion';
import { RootState } from '../../store';
import { setShowIntroModal } from '../../store/slices/uiSlice';
import { updateUser } from '../../store/slices/authSlice';
import { updateUser as updateUserInDb } from '../../services/firestore';
import Button from '../common/Button';
import IntroSlide from './IntroSlide';
import { ASSET_PATHS } from '../../utils/gameConfig';
import { IntroSlide as IntroSlideType } from '../../types';

const IntroModal: React.FC = () => {
  const dispatch = useDispatch();
  const { user } = useSelector((state: RootState) => state.auth);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [dontShowAgain, setDontShowAgain] = useState(false);

  //Default intro slides
  //ToDo: Modify these for prod
  const slides: IntroSlideType[] = [
    {
      id: '1',
      title: 'Welcome to Your Community Forest!',
      content: 'You and your classmates will work together to grow a beautiful forest. Every tree you plant and every drop of water you share helps your community flourish.',
      imagePath: '/assets/images/intro/slide1.png', //ToDo: Add images
    },
    {
      id: '2',
      title: 'Earn Seeds by Learning',
      content: 'Complete daily exercises to earn seeds. Each seed can be planted in your shared forest to grow into a magnificent tree that everyone can enjoy.',
      imagePath: '/assets/images/intro/slide2.png',
    },
    {
      id: '3',
      title: 'Water Trees to Help Them Grow',
      content: 'Chat with our AI guide to earn water. Use your water to help your own trees or your classmates\' trees grow faster. Helping others gives trees an extra boost!',
      imagePath: '/assets/images/intro/slide3.png',
    },
    {
      id: '4',
      title: 'Explore and Grow Together',
      content: 'Walk around your forest, see what your classmates are growing, and watch your community forest become more beautiful each day. Ready to start your journey?',
      imagePath: '/assets/images/intro/slide4.png',
    },
  ];

  const isLastSlide = currentSlide === slides.length - 1;

  const handleNext = () => {
    if (!isLastSlide) {
      setCurrentSlide(prev => prev + 1);
    } else {
      handleFinish();
    }
  };

  const handlePrevious = () => {
    if (currentSlide > 0) {
      setCurrentSlide(prev => prev - 1);
    }
  };

  const handleSkip = () => {
    handleFinish();
  };

  const handleFinish = async () => {
    if (dontShowAgain && user) {
      try {
        await updateUserInDb(user.uid, { showIntroduction: false });
        dispatch(updateUser({ showIntroduction: false }));
      } catch (error) {
        console.error('Error updating user preference:', error);
      }
    }
    //Close the intro modal && show profile
    dispatch(setShowIntroModal(false));
  };

  const backgroundStyle = {
    backgroundImage: `url(${ASSET_PATHS.BACKGROUNDS.MODAL})`,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
  };

  const modalVariants = {
    hidden: { opacity: 0, scale: 0.9 },
    visible: { opacity: 1, scale: 1 },
    exit: { opacity: 0, scale: 0.9 }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={backgroundStyle}
    >
      <div className="absolute inset-0 bg-black/50" />
      
    <motion.div
      variants={modalVariants}
      initial="hidden"
      animate="visible"
      exit="exit"
      className="relative bg-white/95 backdrop-blur-sm rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] flex flex-col border border-powder-200"
    >

        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-powder-200">
          <div className="flex space-x-2">
            {slides.map((_, index) => (
              <div
                key={index}
                className={`w-3 h-3 rounded-full transition-colors ${
                  index === currentSlide ? 'bg-sage-500' : 
                  index < currentSlide ? 'bg-sage-300' : 'bg-powder-200'
                }`}
              />
            ))}
          </div>
          
          <Button
            variant="outline"
            size="sm"
            onClick={handleSkip}
          >
            Skip
          </Button>
        </div>

        {/* Slide Content */}
        <div className="p-8 flex-1 overflow-y-auto">
          <AnimatePresence mode="wait">
            <IntroSlide
              key={currentSlide}
              slide={slides[currentSlide]}
              isActive={true}
            />
          </AnimatePresence>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-powder-200 bg-powder-50/50">
          {isLastSlide && (
            <div className="mb-4">
              <label className="flex items-center space-x-2 text-sm text-powder-700">
                <input
                  type="checkbox"
                  checked={dontShowAgain}
                  onChange={(e) => setDontShowAgain(e.target.checked)}
                  className="rounded border-powder-300 text-sage-500 focus:ring-sage-500"
                />
                <span>Don't show this again</span>
              </label>
            </div>
          )}

          <div className="flex justify-between">
            <Button
              variant="outline"
              onClick={handlePrevious}
              disabled={currentSlide === 0}
            >
              <div className="flex items-center space-x-2">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                <span>Previous</span>
              </div>
            </Button>

            <Button
              variant="primary"
              onClick={handleNext}
            >
              <div className="flex items-center space-x-2">
                <span>{isLastSlide ? "Let's Start!" : 'Next'}</span>
                {!isLastSlide && (
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                )}
              </div>
            </Button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default IntroModal;
